import { useWatchableValue } from '@cotera/client/v0/actions/framework/watchable';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { Clock } from 'lucide-react';
import { useColumnModel } from '../context';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  scheduleDescription,
  scheduleLabel,
  SENSIBLE_CRON_OPTIONS,
  STANDARD_SCHEDULES,
  StandardSchedule,
  valueFromSchedule,
  getScheduleContextFromEventStream,
} from '@cotera/client/v0/utils';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Combobox } from '@cotera/client/v0/components/ui/combobox';
import { CronExpression } from '@cotera/sdk/core';

export const SchedulePopover: React.FC = () => {
  const model = useColumnModel();
  const { when } = useWatchableValue(model.formState);
  const org = useWhoami((s) => s.org);
  const eventStream = useWatchableValue(model.eventStream);

  // Get context from event stream (same logic as backend)
  const scheduleContext = eventStream
    ? getScheduleContextFromEventStream(org.id, eventStream)
    : undefined;

  const scheduleOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'never', label: 'Never' },
    ...SENSIBLE_CRON_OPTIONS.map(([k]) => ({
      value: k,
      label: scheduleLabel(k),
    })),
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost">
          <Clock />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 w-full">
          <Combobox
            value={valueFromSchedule(when)}
            items={scheduleOptions}
            onChange={(v) => {
              if (STANDARD_SCHEDULES.includes(v as StandardSchedule)) {
                model.formState.set({
                  ...model.formState.snapshot(),
                  when: v as StandardSchedule,
                });
              } else {
                const value = CronExpression[v as keyof typeof CronExpression];
                if (value !== undefined) {
                  model.formState.set({
                    ...model.formState.snapshot(),
                    when: { cron: value },
                  });
                }
              }
            }}
          />
          <div className="text-sm text-muted-foreground">
            {scheduleDescription(when, 'tool', scheduleContext)}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
